import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import timer from 'app/managecustomer/Timer';
import { PriorityViewModel } from 'app/models/PriorityViewModel';
import { StoreService } from '../../../services/store.service';
import { CustomerContactPreferencesService } from '../../../services/customer-contact-preferences.service';
import { MerchantFieldsService } from '../../../services/merchant-fields.service';
import { UserService } from '../../../services/user.service';
import { PartnerSupportLevelService } from '../../../services/partner-support-level.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import Utils from '../../../shared/utils';
import { FrontService } from '../../../services/front.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { CustomerUserService } from '../../../services/customer-user.service';
import { forkJoin, of, throwError } from 'rxjs';

@Component({
  selector: 'app-components-managecustomer-customerinformation',
  templateUrl: './CustomerInformation.component.html',
  styleUrls: ['./CustomerInformation.component.scss']
})
export class CustomerInformationComponent implements OnInit {
  @Input() customer: Boo.Objects.Customer;
  @Input() partner: Boo.Objects.Partner;
  @Input() page: string;
  @Input() showStoreLink = false;
  @Input() showTicketSaveTimer = false;

  @Output() pageChangeEvent = new EventEmitter<string>();
  @Output() customerSaved = new EventEmitter<void>();
  @Output() refreshCustomer = new EventEmitter<void>();

  @ViewChild('draftValidationModal') draftValidationModal: TemplateRef<any>;

  utils = Utils;
  title = 'Customer Information';
  isLoading = false;
  contact: Boo.Objects.CustomerUser;
  contactNote: string;
  merchantFields: Boo.Objects.CustomerMerchantFields;
  loggedInUser: Boo.Objects.User;
  partnerSupportLevelName: string;
  customerTime: string;
  lastTicketSaveTime: string;
  canEditMerchantInformation = false;
  countries: Boo.Objects.Country[] = [];
  partnerGroups: Boo.Objects.Group[] = [];
  ordersSnapshot: Boo.ApisExternal.Store.Objects.Order[];
  subscriptionsSnapshot: Boo.ApisExternal.Store.Objects.Subscription[];
  orderStatus: boolean;
  today: Date = new Date();
  nextRenewalDate: Date;
  emailContactLabel: string;
  csrEmailAddress: string;
  supportPhoneNumberCountryCode: string;

  isDevMode = environment.testConfig.testMessage === 'Dev Mode';

  private updateTimersTimoutInterval: number;
  private emailModifierConfig: Record<number, EmailModifierConfig> = {};

  constructor(
    private storeService: StoreService,
    private customerContactPreferencesService: CustomerContactPreferencesService,
    private merchantFieldsService: MerchantFieldsService,
    private userService: UserService,
    private partnerSupportLevelService: PartnerSupportLevelService,
    private sessionStorageService: SessionStorageService,
    private frontService: FrontService,
    private modalService: NgbModal,
    private customerUserService: CustomerUserService) { }

  ngOnInit(): void {
    forkJoin({
      user: this.sessionStorageService.getUser(),
      staticData: this.sessionStorageService.getStaticData(),
      contactPreferences: this.customerContactPreferencesService.get(this.customer.CustomerId),
      merchantFields: this.merchantFieldsService.get(this.customer.CustomerId),
      ordersSnapshot: this.storeService.getOrdersSnapshot(this.customer.CustomerId),
      subscriptionsSnapshot: this.storeService.getSubscriptionsSnapshot(this.customer.CustomerId)
    })
      .subscribe(({ user, staticData, contactPreferences, merchantFields, ordersSnapshot, subscriptionsSnapshot }) => {
        this.loggedInUser = user;
        this.countries = staticData.Countries;
        this.partnerGroups = staticData.PartnerGroups;
        this.merchantFields = merchantFields;
        this.ordersSnapshot = ordersSnapshot;
        this.subscriptionsSnapshot = subscriptionsSnapshot;
        this.nextRenewalDate = subscriptionsSnapshot
          .filter(x => x.IntervalMonths > 0)
          .pop()
          ?.Next;

        this.canEditMerchantInformation = user.PartnerUsers.some(pu => pu.UserLevelId === Boo.Objects.Enums.UserLevelEnum.CustomerService && pu.IsReadonly === false);
        this.csrEmailAddress = this.customer.Csr ? this.getCsrEmailAddress(this.customer) : this.partner.SupportEmailAddress;
        this.supportPhoneNumberCountryCode = this.countries.find(x => x.CountryId === this.partner.CountryId)?.Abbreviation;
        this.contactNote = contactPreferences.Note || '';
        this.setContactEmail();
        this.loadPartnerSupportLevelName();
        this.updateTimersTimoutInterval = setInterval(() => this.updateTimers(), 1000);
      });
  }

  editCustomerContacts(): void {
    PriorityViewModel.show('app-components-managecustomer-customer-users-priority',
      {
        customer: this.customer,
        user: this.loggedInUser,
        partner: this.partner
      })
      .then((data: { users: KnockoutObservableArray<CustomerUserObservable> }) => {
        if (data.users) {
          this.customer.Users = ko.mapping.toJS(data.users());
          this.setContactEmail();
        }

        // call customerSavedCallback because customer.Users may need to be refreshed
        this.customerSaved.emit();
      });
  }

  updateTimers(): void {
    this.customerTime = timer.getFormattedTimeSinceStart();
    this.lastTicketSaveTime = timer.getFormattedTimeSinceLastTicketSaveTime();
  }

  deactivate(): void {
    clearInterval(this.updateTimersTimoutInterval);
  }

  viewReportingSite() {
    this.userService.csrReportingAccess(this.customer.CustomerId)
      .subscribe({
        next: token => window.open(`${this.customer.Partner.ReportingUrl}/?t=${token}`, '_blank'),
        error: err => toastr.error(err)
      });
  }

  // This method is temporary and intentionally not using config or partner settings at this time
  viewReportingSiteV3() {
    this.userService.csrReportingAccess(this.customer.CustomerId)
      .subscribe({
        next: token => window.open(`http://localhost:4200/?t=${token}`, '_blank'),
        error: err => toastr.error(err)
      });
  }

  loadPartnerSupportLevelName(): any {
    const id = this.partner ? this.partner.SupportLevelId : 0;
    if (id <= 0) {
      this.partnerSupportLevelName = 'None';
      return;
    }
    this.partnerSupportLevelService.getActive()
      .subscribe({
        next: allLevels => {
          const level = allLevels.find(x => x.PartnerSupportLevelId === id);
          this.partnerSupportLevelName = level ? level.Name : 'None';
        },
        error: err => toastr.error(err)
      });
  }

  getCsrEmailAddress(customer: Boo.Objects.Customer): any {
    const emailModifier = this.emailModifierConfig[customer.PartnerId];
    if (emailModifier) {
      return customer.Csr.Username.replace(emailModifier.replaceText, emailModifier.withText);
    } else {
      return customer.Csr.Username;
    }
  }

  getPartnerGroup(partnerGroup: Boo.Objects.Enums.PartnerGroupEnum) {
    return this.partnerGroups.find(x => x.GroupId === partnerGroup).PartnerGroups.map(x => x.PartnerId);
  }

  openCustomerUsersTab(): void {
    this.pageChangeEvent.emit('Customer Contact')
  }

  navigateToStore(): void {
    this.storeService.sso(this.customer.CustomerId, '/', `#/customerservice/managecustomer/${this.customer.CustomerId}`)
      .subscribe({
        next: url => window.location.href = url,
        error: err => toastr.error(err)
      });
  }

  setContactEmail() {
    this.contact = this.customer.Users.find(x => x.EmailRecipientType == Boo.Objects.Enums.EmailRecipientTypes.To && x.CanContact) ?? this.customer.Users.find(x => x.CanContact);
    const recipientCount = this.customer.Users.filter(x => x.EmailRecipientType != null && x.CanContact).length;
    this.emailContactLabel = this.contact?.Username + (recipientCount < 2 ? '' : ' (+' + (recipientCount - 1) + ')');
  }

  createDraft(): void {
    this.customerUserService.getForCustomer(this.customer.CustomerId)
      .pipe(
        switchMap(currentUsers => {
          this.validateCustomerUsers(currentUsers);
          return of({});
        }),
        switchMap(() => this.frontService.getCustomerChannel(this.customer.CustomerId)),
        switchMap(customerFrontChannel => customerFrontChannel ? of(customerFrontChannel) : this.frontService.getPartnerChannel(this.customer.PartnerId)),
        switchMap(channel => {
          this.validatePartnerChannel(channel);
          toastr.success('Redirecting to Front');
          return this.frontService.createDraft(channel.Id, this.customer.CustomerId)
        }),
        switchMap(conversationId => window.open(`${environment.urlConfig.frontAppUrl}/go?link=%2Fopen%2F${conversationId}`, '_blank')),
        catchError(err => throwError(() => new Error(err)))
      )
      .subscribe({
        error: err => {
          if (err.message.includes('No To contact found')) {
            this.modalService.open(this.draftValidationModal);
          }
          else {
            toastr.error(err);
          }
        }
      });
  }

  private validateCustomerUsers(customerUsers: Boo.Objects.CustomerUser[]): void {
    if (customerUsers.filter((x: Boo.Objects.CustomerUser) => x.EmailRecipientType == Boo.Objects.Enums.EmailRecipientTypes.To && x.CanContact).length === 0) {
      throw 'No To contact found';
    }
  }

  private validatePartnerChannel(channel: app.tsmodels.interfaces.FrontChannel): void {
    if (!channel) {
      throw 'No Front channel set for this partner, please contact support.';
    }
  }
}

interface EmailModifierConfig {
  replaceText: string;
  withText: string;
}
